<template>
  <HrPageLayout
    title="HR Case Management"
    subtitle="Open cases, assign handlers, comment, escalate, and close with resolution"
    icon="bx bx-briefcase-alt"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'HR Cases' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-plus"></i> Open Case
      </button>
    </template>

    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-1"><div class="hr-metric-content">
              <p class="hr-metric-label">Total</p><h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-4"><div class="hr-metric-content">
              <p class="hr-metric-label">Open</p><h2 class="hr-metric-value">{{ metrics.open_count || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-3"><div class="hr-metric-content">
              <p class="hr-metric-label">In progress</p><h2 class="hr-metric-value">{{ metrics.in_progress_count || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-2"><div class="hr-metric-content">
              <p class="hr-metric-label">Escalated</p><h2 class="hr-metric-value">{{ metrics.escalated_count || 0 }}</h2>
            </div></div>
    </div>

    <div class="hr-chart-card hr-mb-4">
      <div class="hr-chart-body hr-py-3">
        <div class="mod-filters">
          <select v-model="filterStatus" class="hr-select" @change="loadCases">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="escalated">Escalated</option>
            <option value="closed">Closed</option>
          </select>
          <input v-model="search" type="search" class="hr-input" placeholder="Search..." @input="debouncedLoad" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
    <div v-else class="hr-table-wrapper">
      <table class="hr-table hr-table-hover">
        <thead>
          <tr><th>Case #</th><th>Employee</th><th>Title</th><th>Priority</th><th>Assignee</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in cases" :key="c.id">
            <td><code>{{ c.case_number }}</code></td>
            <td>{{ c.employee_name }}</td>
            <td>{{ c.title }}</td>
            <td><span class="hr-badge" :class="priorityClass(c.priority)">{{ c.priority }}</span></td>
            <td>{{ c.assignee_name || '—' }}</td>
            <td><span class="hr-badge hr-badge-info">{{ c.status }}</span></td>
            <td class="hr-text-end"><button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="openDetail(c)">Open</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create -->
    <div v-if="showCreate" class="hr-modal-overlay" @click.self="showCreate = false">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header"><h5>Open HR case</h5><button class="hr-modal-close" @click="showCreate = false"><i class="bx bx-x"></i></button></div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-form-grid-2">
              <div class="hr-field-full">
                <label class="hr-form-label">Employee *</label>
                <input v-model="empSearch" class="hr-input" @input="searchEmployees" placeholder="Search..." />
                <div v-if="empResults.length" class="mod-search-results">
                  <button v-for="e in empResults" :key="e.id" type="button" class="mod-search-item" @click="pickEmployee(e)">{{ e.fullName }}</button>
                </div>
                <p v-if="createForm.employeeName" class="hr-small hr-text-success">Selected: {{ createForm.employeeName }}</p>
              </div>
              <div>
                <label class="hr-form-label">Category *</label>
                <select v-model="createForm.categoryId" class="hr-select">
                  <option value="">Select...</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Priority</label>
                <select v-model="createForm.priority" class="hr-select">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Assign to</label>
                <select v-model="createForm.assignedTo" class="hr-select">
                  <option value="">Unassigned</option>
                  <option v-for="u in hrUsers" :key="u.userID" :value="u.userID">{{ u.names }}</option>
                </select>
              </div>
              <div class="hr-field-full"><label class="hr-form-label">Title *</label><input v-model="createForm.title" class="hr-input" /></div>
              <div class="hr-field-full"><label class="hr-form-label">Description *</label><textarea v-model="createForm.description" class="hr-input" rows="4"></textarea></div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button class="hr-btn hr-btn-secondary" @click="showCreate = false">Cancel</button>
            <button class="hr-btn hr-btn-primary" :disabled="saving" @click="submitCreate">Open case</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail -->
    <div v-if="showDetail && detail" class="hr-modal-overlay" @click.self="closeDetail">
      <div class="hr-modal-dialog hr-modal-xl">
        <div class="hr-modal-content">
          <div class="hr-modal-header"><h5>{{ detail.case.case_number }} — {{ detail.case.title }}</h5><button class="hr-modal-close" @click="closeDetail"><i class="bx bx-x"></i></button></div>
          <div class="hr-modal-body">
            <p><strong>{{ detail.case.employee_name }}</strong> · {{ detail.case.category_name }} · Priority: {{ detail.case.priority }}</p>
            <p class="hr-text-muted">{{ detail.case.description }}</p>

            <div class="mod-section">
              <h6>Comments</h6>
              <div v-for="c in detail.comments" :key="c.id" class="mod-comment" :class="{ internal: c.is_internal == 1 }">
                <strong>{{ c.author_name }}</strong> <span class="hr-text-muted hr-small">{{ formatDt(c.created_at) }}</span>
                <span v-if="c.is_internal == 1" class="hr-badge hr-badge-secondary hr-ml-1">Internal</span>
                <p>{{ c.comment }}</p>
              </div>
              <textarea v-model="commentForm.comment" class="hr-input hr-mt-2" rows="2" placeholder="Add comment..."></textarea>
              <label class="hr-small"><input type="checkbox" v-model="commentForm.isInternal" /> Internal note</label>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-primary hr-ml-2" @click="addComment">Post</button>
            </div>

            <div class="mod-section" v-if="detail.case.status !== 'closed'">
              <h6>Escalate</h6>
              <select v-model="escalateForm.escalatedTo" class="hr-select hr-mb-2">
                <option value="">Select HR user...</option>
                <option v-for="u in hrUsers" :key="u.userID" :value="u.userID">{{ u.names }}</option>
              </select>
              <textarea v-model="escalateForm.reason" class="hr-input" rows="2" placeholder="Reason for escalation"></textarea>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-warning hr-mt-2" @click="escalate">Escalate</button>
            </div>

            <div class="mod-section" v-if="detail.escalations.length">
              <h6>Escalation history</h6>
              <ul><li v-for="e in detail.escalations" :key="e.id">{{ e.escalated_by_name }} → {{ e.escalated_to_name }}: {{ e.reason }}</li></ul>
            </div>

            <div v-if="detail.case.status !== 'closed'" class="mod-section">
              <textarea v-model="closeSummary" class="hr-input" rows="2" placeholder="Resolution summary"></textarea>
              <button type="button" class="hr-btn hr-btn-success hr-mt-2" @click="closeCase">Close case</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "HrCaseManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false, saving: false, cases: [], metrics: {}, categories: [], hrUsers: [],
      filterStatus: "", search: "", debounceTimer: null,
      showCreate: false, showDetail: false, detail: null,
      empSearch: "", empResults: [],
      createForm: { employeeId: "", employeeName: "", categoryId: "", title: "", description: "", priority: "normal", assignedTo: "" },
      commentForm: { comment: "", isInternal: false },
      escalateForm: { escalatedTo: "", reason: "" },
      closeSummary: "",
    };
  },
  mounted() {
    this.loadMeta();
    this.loadCases();
  },
  methods: {
    debouncedLoad() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => this.loadCases(), 350); },
    async loadMeta() {
      const catD = new FormData();
      catD.append("function", "getHrCaseCategories");
      const catRes = await execute(catD, authUrl);
      if (catRes?.data?.success) this.categories = catRes.data.data?.categories || [];

      const userD = new FormData();
      userD.append("function", "getHrModuleUsers");
      const uRes = await execute(userD, authUrl);
      if (uRes?.data?.success) this.hrUsers = uRes.data.data?.users || [];
    },
    async loadCases() {
      this.loading = true;
      const d = new FormData();
      d.append("function", "getHrCases");
      if (this.filterStatus) d.append("status", this.filterStatus);
      if (this.search) d.append("search", this.search);
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) {
          this.cases = res.data.data?.cases || [];
          this.metrics = res.data.data?.metrics || {};
        }
      } finally { this.loading = false; }
    },
    openCreate() { this.showCreate = true; this.createForm = { employeeId: "", employeeName: "", categoryId: "", title: "", description: "", priority: "normal", assignedTo: "" }; },
    async searchEmployees() {
      const d = new FormData(); d.append("function", "searchEmployeesForHrModule"); d.append("q", this.empSearch);
      const res = await execute(d, authUrl);
      this.empResults = res?.data?.data?.employees || [];
    },
    pickEmployee(e) { this.createForm.employeeId = e.id; this.createForm.employeeName = e.fullName; this.empResults = []; },
    async submitCreate() {
      if (!this.createForm.employeeId || !this.createForm.categoryId || !this.createForm.title || !this.createForm.description) {
        toast.error("Fill required fields"); return;
      }
      this.saving = true;
      const d = new FormData(); d.append("function", "createHrCase");
      ["employeeId","categoryId","title","description","priority","assignedTo"].forEach(k => { if (this.createForm[k]) d.append(k, this.createForm[k]); });
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) { toast.success("Case opened"); this.showCreate = false; this.loadCases(); }
        else toast.error(res?.data?.message);
      } finally { this.saving = false; }
    },
    async openDetail(c) {
      const d = new FormData(); d.append("function", "getHrCaseDetail"); d.append("id", c.id);
      const res = await execute(d, authUrl);
      if (res?.data?.success) { this.detail = res.data.data; this.showDetail = true; }
    },
    closeDetail() { this.showDetail = false; this.detail = null; this.loadCases(); },
    async post(fn, fields) {
      const d = new FormData(); d.append("function", fn);
      Object.entries(fields).forEach(([k,v]) => { if (v != null && v !== "") d.append(k, v); });
      const res = await execute(d, authUrl);
      if (res?.data?.success) { toast.success("Saved"); await this.openDetail({ id: this.detail.case.id }); return true; }
      toast.error(res?.data?.message); return false;
    },
    addComment() {
      this.post("addHrCaseComment", { caseId: this.detail.case.id, comment: this.commentForm.comment, isInternal: this.commentForm.isInternal ? 1 : 0 });
      this.commentForm = { comment: "", isInternal: false };
    },
    escalate() {
      this.post("escalateHrCase", { caseId: this.detail.case.id, ...this.escalateForm });
    },
    closeCase() {
      this.post("closeHrCase", { id: this.detail.case.id, resolutionSummary: this.closeSummary });
    },
    priorityClass(p) {
      return { urgent: "hr-badge-danger", high: "hr-badge-warning", normal: "hr-badge-info", low: "hr-badge-secondary" }[p] || "";
    },
    formatDt(v) { return v ? new Date(v).toLocaleString() : ""; },
  },
};
</script>

<style scoped>
.mod-filters { display: flex; gap: 12px; flex-wrap: wrap; }
.mod-search-results { border: 1px solid #e2e8f0; border-radius: 8px; max-height: 140px; overflow: auto; }
.mod-search-item { display: block; width: 100%; padding: 8px 12px; border: none; background: #fff; text-align: left; cursor: pointer; }
.mod-search-item:hover { background: #f1f5f9; }
.mod-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
.mod-comment { padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.mod-comment.internal { background: #fffbeb; padding: 8px; border-radius: 6px; }
.hr-modal-xl { max-width: 900px; width: 95%; }
</style>
